import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Helmet } from "react-helmet-async";
import tw from "twin.macro";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import { css } from "styled-components/macro"; //eslint-disable-line

const Container = tw.div`max-w-3xl mx-auto px-6 py-20 text-gray-800`;
const Heading = tw.h1`text-4xl md:text-5xl font-black text-primary-900 text-center mb-12 leading-snug`;
const Paragraph = tw.p`mt-6 text-lg leading-relaxed`;
const CallToAction = tw.div`mt-16 p-8 rounded-xl bg-gray-100 text-center`;
const CtaHeading = tw.p`text-2xl font-bold text-gray-900`;
const CtaButtons = tw.div`mt-6 flex flex-col sm:flex-row gap-4 justify-center`;
const CtaPrimary = tw(Link)`px-8 py-4 rounded-full bg-primary-500 text-white font-bold hocus:bg-primary-700 transition duration-300`;
const CtaSecondary = tw(Link)`px-8 py-4 rounded-full border-2 border-primary-500 text-primary-500 font-bold hocus:bg-primary-500 hocus:text-white transition duration-300`;
const Highlight = tw.span`text-primary-500 font-semibold`;
const Section = tw.div`mt-16`;
const Subheading = tw.h2`text-2xl font-bold text-gray-900 mt-12`;
const BannerImage = styled.img`
  ${tw`mt-8 w-full h-72 md:h-96 object-cover rounded-lg`}
  object-position: 50% 40%; /* X% Y% */
`;

export default () => {
  return (
    <AnimationRevealPage>
      <Helmet>
        <title>About Us | EthosHub</title>
        <meta name="description" content="EthosHub was built by people who’ve experienced workplace misconduct firsthand. Our mission: make safe, respectful workplace culture achievable and affordable for every organisation." />
        <link rel="canonical" href="https://www.ethoshub.io/AboutUs" />
        <meta property="og:title" content="About Us | EthosHub" />
        <meta property="og:description" content="We built EthosHub because we were tired of seeing harassment, bullying and discrimination brushed aside. Learn why we exist and what drives us." />
        <meta property="og:url" content="https://www.ethoshub.io/AboutUs" />
      </Helmet>
      <Header />

      <Container>
        <Heading>Built by People Who’ve Lived It</Heading>
      <div tw="w-full mb-12">
        <BannerImage
          src="https://images.unsplash.com/photo-1676276383594-2f1cdcdf9604?q=80&w=1887&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
          alt="About Us Banner"
        />
      </div>
        <Paragraph>
          We started <Highlight>EthosHub</Highlight> because we were tired — tired of witnessing and experiencing culture-breaking behaviours that were too often brushed aside. <Highlight>Harassment. Bullying. Discrimination.</Highlight> And the silence that followed when there was no safe or effective way to report it.
        </Paragraph>

        <Paragraph>
          As professionals and leaders, we saw how damaging these moments could be — not just for individuals, but for entire teams and organisations. And when we searched for solutions, we found a market filled with bloated, expensive platforms full of hidden fees and tiered pricing models that excluded the very organisations that needed them most.
        </Paragraph>

        <Section>
          <Subheading>From Frustration to Function</Subheading>
          <Paragraph>
            EthosHub began as an internal tool — a way to support our own team. We needed something simple, secure, and genuinely accessible. A tool people could trust, even when reporting anonymously. Something that wouldn’t require a huge IT budget or complex setup to be effective.
          </Paragraph>

          <Paragraph>
            It worked. So we made it better. We listened, iterated, and built a full-featured, scalable SaaS product designed around the real-world needs of teams like yours. Now, any organisation — big or small — can adopt EthosHub without compromise.
          </Paragraph>
        </Section>

        <Section>
          <Subheading>A Safer Culture Shouldn’t Be a Luxury</Subheading>
          <Paragraph>
            Our mission is clear: <Highlight>make respectful, safe workplace culture achievable and affordable for everyone</Highlight>. From day one, every feature is included. No bolt-ons. No upsells. No fine print. Just the tools you need to take reports seriously, support your people, and act with integrity.
          </Paragraph>

          <Paragraph>
            Because no one should have to choose between cost and compliance. Because <Highlight>safety should scale</Highlight>. And because future workplaces — the ones our children will grow into — deserve better.
          </Paragraph>
        </Section>

        <Section>
          <Subheading>Built for People. Backed by Purpose.</Subheading>
          <Paragraph>
            We’re not a faceless SaaS company. We’re a small team of founders who’ve seen the gaps and decided to do something about it. EthosHub is our contribution to a better future — one where:
          </Paragraph>

          <ul tw="list-disc pl-6 mt-4 space-y-2 text-lg text-gray-800">
            <li>Employees feel safe to speak up.</li>
            <li>Leaders can act with insight and accountability.</li>
            <li>Harassment, assault, and discrimination aren't ignored or repeated.</li>
          </ul>

          <Paragraph>
            Together, we can build work environments worth passing on — where safety isn’t optional, and silence is never the only option.
          </Paragraph>

          <CallToAction>
            <CtaHeading>Ready to take the first step?</CtaHeading>
            <CtaButtons>
              <CtaPrimary to="/contact">Book a demo</CtaPrimary>
              <CtaSecondary to="/PricingPage">See pricing</CtaSecondary>
            </CtaButtons>
          </CallToAction>
        </Section>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};